/**
 * RequireRole — Route-level permission guard for PHSIMS WebAdmin
 * Author: Iswe Solutions Team
 *
 * Enforces page-level access control based on the user's stored permissions.
 * Roles are dynamic (created in Administration > Roles) with pages assigned
 * per role. No hardcoded role hierarchy — permissions come from the database.
 *
 * Usage:
 *   <Route path="/ai/malaria" element={
 *     <RequireRole pagePath="/ai/malaria"><MalariaPage /></RequireRole>
 *   } />
 */

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import LockIcon from '@mui/icons-material/Lock';
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate } from 'react-router-dom';
import { Authenticated } from 'react-admin';
import { hasPageAccess } from '../../config/menuConfig';

interface StoredUser {
  role?: string;
  is_superuser?: boolean;
  permissions?: {
    pages?: string[];
    menu_sections?: string[];
    features?: Record<string, boolean>;
  };
}

function getUser(): StoredUser | null {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export interface RequireRoleProps {
  /** Check page-path permission from user's assigned pages list. */
  pagePath?: string;
  /** Required feature flag (e.g. "user_management"). */
  feature?: string;
  children: React.ReactNode;
}

/**
 * Route guard that enforces permission checks based on the user's
 * stored permissions (assigned via their role in Administration > Roles).
 * Super users and admins with pages: ["*"] bypass all checks.
 */
const RequireRole: React.FC<RequireRoleProps> = ({
  pagePath,
  feature,
  children,
}) => {
  const navigate = useNavigate();
  const user = getUser();

  // Not logged in — let Authenticated handle redirect
  if (!user) {
    return <Authenticated>{children}</Authenticated>;
  }

  const userRole = user.role || 'viewer';
  const isSuperUser = user.is_superuser === true || userRole === 'super_user';

  // Super users bypass all checks
  if (isSuperUser) {
    return <Authenticated>{children}</Authenticated>;
  }

  const perms = user.permissions;
  const allowedPages = perms?.pages || [];

  // Page-path permission check
  if (pagePath && !hasPageAccess(pagePath, allowedPages)) {
    return (
      <Authenticated>
        <AccessDenied
          reason="You don't have permission to access this page."
          onGoHome={() => navigate('/')}
        />
      </Authenticated>
    );
  }

  // Feature flag check
  if (feature) {
    const features = perms?.features || {};
    if (!(features as Record<string, boolean>)[feature]) {
      return (
        <Authenticated>
          <AccessDenied
            reason={`The "${feature}" feature is not enabled for your account.`}
            onGoHome={() => navigate('/')}
          />
        </Authenticated>
      );
    }
  }

  return <Authenticated>{children}</Authenticated>;
};

/**
 * Access Denied screen shown when a user navigates to a page they
 * don't have permission for.
 */
const AccessDenied: React.FC<{ reason: string; onGoHome: () => void }> = ({
  reason,
  onGoHome,
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '60vh',
      p: { xs: 2, sm: 4 },
    }}
  >
    <Paper
      elevation={0}
      sx={{
        maxWidth: 480,
        width: '100%',
        p: { xs: 3, sm: 5 },
        textAlign: 'center',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          bgcolor: 'error.light',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 3,
        }}
      >
        <LockIcon sx={{ fontSize: 32, color: 'error.contrastText' }} />
      </Box>

      <Typography variant="h5" fontWeight={700} gutterBottom>
        Access Denied
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {reason}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Contact your administrator if you believe you should have access to this page.
      </Typography>

      <Button
        variant="contained"
        startIcon={<HomeIcon />}
        onClick={onGoHome}
        sx={{
          bgcolor: '#0d5c2e',
          '&:hover': { bgcolor: '#148a44' },
          textTransform: 'none',
          px: 4,
          py: 1,
        }}
      >
        Go to Home
      </Button>
    </Paper>
  </Box>
);

export default RequireRole;
